/**
 * Applying a post-check-in serial correction to a stored inspection.
 *
 * The serial has no column of its own — it lives inside the draft job's
 * `cr001_inspection_json` blob — so a correction is a whole-blob PATCH with
 * one field swapped. Keeping that spread here (rather than in the dialog)
 * means the rule "a serial and 'not legible' are a pair" is testable and is
 * the same wherever the correction is raised from.
 *
 * Deliberately does NOT touch `inspectionStatus`: a correction must not
 * re-open a completed inspection the way stepping through the wizard does.
 */
import type { CheckInInspection } from '@techgitter/contracts';

import type { InspectionSchemaEntry } from './inspectionSchema';

export interface SerialCorrection {
  serialNumber: string;
  serialNotLegible: boolean;
}

/**
 * A copy of `inspection` carrying the corrected serial.
 *
 * Empty/whitespace serials normalise to null (matching how the wizard
 * stores "no serial"), and a serial that is actually present always clears
 * the not-legible flag — you cannot have both. The flag is only written
 * back for device types whose inspection member declares it.
 */
export function applySerialCorrection(
  inspection: CheckInInspection,
  schema: InspectionSchemaEntry,
  correction: SerialCorrection,
): CheckInInspection {
  const serialNumber = correction.serialNumber.trim() || null;
  const next = { ...inspection, serialNumber } as CheckInInspection;
  if (schema.supportsSerialNotLegible) {
    (next as { serialNotLegible: boolean }).serialNotLegible = serialNumber
      ? false
      : correction.serialNotLegible;
  }
  return next;
}

/**
 * Null when the correction is acceptable, else the message to show.
 *
 * Mirrors `validateIdentifyStep`'s serial rule so a correction can't save a
 * value the wizard would have refused — the manufacturer matters because
 * it's what makes the serial optional on a Custom desktop.
 */
export function validateSerialCorrection(
  schema: InspectionSchemaEntry,
  manufacturer: string | null | undefined,
  correction: SerialCorrection,
): string | null {
  if (correction.serialNumber.trim()) return null;
  const optional =
    (correction.serialNotLegible && schema.supportsSerialNotLegible) ||
    !!schema.serialOptional?.(manufacturer);
  return optional ? null : 'Serial number is required';
}

/** True when the correction would leave the inspection unchanged. */
export function isSerialUnchanged(
  inspection: CheckInInspection,
  schema: InspectionSchemaEntry,
  correction: SerialCorrection,
): boolean {
  const next = applySerialCorrection(inspection, schema, correction);
  const flagOf = (i: CheckInInspection) =>
    'serialNotLegible' in i ? Boolean((i as { serialNotLegible?: boolean }).serialNotLegible) : false;
  return (
    (next.serialNumber ?? null) === (inspection.serialNumber ?? null) &&
    flagOf(next) === flagOf(inspection)
  );
}
